// This is a stylized rich text block
import { RenderNode } from '@contentful/rich-text-react-renderer'
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types'
import classnames from 'classnames'
import Image from '@/components/contentful/Image'
import Highlights from '@/components/pages/CampaignPages/DetailComponents/Highlights'
import colors from '@/config/colors'
import { report, richText } from '@/utils'
import AnimateIn from '../AnimateIn'
import GuildLiveCount from '../GuildLiveCount'
import Type from '../Type'
import VideoBox from '../VideoBox'

export interface Props {
  campaignSlug?: string
  className?: string
  document: Document
  optionOverrides?: RenderNode
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const options = (campaignSlug = '', overrides?: RenderNode) => ({
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const fields = node?.data?.target?.fields
      const contentType = fields?.file?.contentType || ''

      if (fields && contentType.match(/^image/)) {
        return (
          <AnimateIn
            variant="fadeIn"
            className="-mx-6 mb-8 w-screen lg:mx-0 lg:w-full"
          >
            <Image
              className="w-full"
              image={node.data.target}
              quality={100}
              width={975}
              lazy
            />
          </AnimateIn>
        )
      } else {
        // TODO: support other embedded asset types
        report.log(`unsupported embedded asset: ${contentType}`)
      }
      return null
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const { fields, sys } = node?.data?.target || {}
      const contentType = sys?.contentType?.sys?.id || ''

      if (contentType === 'videoBlock') {
        return (
          <VideoBox
            className="-mx-6 mb-8 lg:mx-0"
            campaignSlug={campaignSlug}
            id={`videobox-${fields.id}`}
            video={fields.id}
            service={fields.service}
            aspectRatio={fields.aspectRatio}
          />
        )
      } else if (contentType === 'guildLiveCount') {
        return <GuildLiveCount />
      } else {
        // TODO: support other embedded entry types
        report.log(`unsupported embedded entry: ${contentType}`)
      }
      return null
    },
    // h2-h4 are the only supported headings in Contentful at this time
    [BLOCKS.HEADING_2]: (node: any, children: any) => {
      return (
        <Type as="h2" variant="title-lg" className="mb-4 font-semibold">
          {children}
        </Type>
      )
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return (
        <Type as="h3" variant="title-md" className="mb-4 font-semibold">
          {children}
        </Type>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return (
        <Type as="h4" variant="title-sm" className="mb-4 font-semibold">
          {children}
        </Type>
      )
    },
    [BLOCKS.HR]: () => {
      return <hr className="mb-8" style={{ borderColor: colors.gray }} />
    },
    [BLOCKS.OL_LIST]: (node: any) => {
      const messages = node.content.map((listItem: any) =>
        // render the inner <p> as the message
        richText(listItem.content[0])
      )
      return <Highlights className="mb-8" highlights={messages} />
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Type as="p" variant="paragraph-md" className="mb-4 last:mb-0">
          {children}
        </Type>
      )
    },
    [BLOCKS.QUOTE]: (node: any, children: any) => {
      return (
        <blockquote className="mb-8 border-l-[5px] border-solid border-l-lightGray pl-6">
          {children}
        </blockquote>
      )
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className="my-8 ml-10 mr-0 list-disc">{children}</ul>
    },
    [INLINES.HYPERLINK]: ({ data }: any, children: any) => {
      return (
        <a
          className="text-oxide-2 hover:text-oxide-1"
          target="_blank"
          rel="noreferrer"
          href={data.uri}
        >
          {children}
        </a>
      )
    },
    ...overrides,
  },
  renderText: (text: string) => {
    // converts \n characters to <br /> elements
    return text.split('\n').reduce((children: any, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
})
/* eslint-enable @typescript-eslint/no-explicit-any */

const TextBlock = ({
  campaignSlug,
  className,
  document,
  optionOverrides,
}: Props) => {
  return (
    <div className={classnames('mb-4', className)}>
      {richText(document, options(campaignSlug, optionOverrides))}
    </div>
  )
}

export default TextBlock
